import { Container } from 'pixi.js';
import type { ScratchSymbol } from '../../model/scratch/ScratchTypes';
import { ScratchCell } from './ScratchCell';

export interface ScratchGridOptions {
  cellCount: number;
  columnCount: number;
  /** Cell width/height in px. Default 88. */
  cellSize?: number;
  /** Pixel spacing between cells. Default 12. */
  spacing?: number;
  foilColor?: number;
  onPick: (index: number) => void;
}

/**
 * The scratch-card grid widget — N cells laid out in a fixed-column grid.
 * Owns each {@link ScratchCell} and forwards taps via {@code onPick}.
 *
 * <p>Sizing: {@code totalWidth} / {@code totalHeight} return the grid's
 * natural pixel size so the host scene can scale-to-fit on narrow viewports
 * (mirrors {@code PickEmGrid}).
 */
export class ScratchGrid extends Container {
  private readonly cells: ScratchCell[] = [];
  private readonly columnCount: number;
  private readonly cellSize: number;
  private readonly spacing: number;

  constructor(options: ScratchGridOptions) {
    super();
    this.columnCount = Math.max(1, options.columnCount);
    this.cellSize = options.cellSize ?? 88;
    this.spacing = options.spacing ?? 12;

    for (let i = 0; i < options.cellCount; i++) {
      const cell = new ScratchCell({
        size: this.cellSize,
        index: i,
        foilColor: options.foilColor,
        onTap: options.onPick,
      });
      const row = Math.floor(i / this.columnCount);
      const col = i % this.columnCount;
      cell.x = col * (this.cellSize + this.spacing) + this.cellSize / 2;
      cell.y = row * (this.cellSize + this.spacing) + this.cellSize / 2;
      this.cells.push(cell);
      this.addChild(cell);
    }
  }

  totalWidth(): number {
    const cols = Math.min(this.cells.length, this.columnCount);
    return cols * this.cellSize + (cols - 1) * this.spacing;
  }

  totalHeight(): number {
    const rows = Math.ceil(this.cells.length / this.columnCount);
    return rows * this.cellSize + (rows - 1) * this.spacing;
  }

  reveal(index: number, symbol: ScratchSymbol): Promise<void> {
    const cell = this.cells[index];
    if (!cell) return Promise.resolve();
    return cell.reveal(symbol);
  }

  destroyGrid(): void {
    for (const cell of this.cells) cell.destroyCell();
  }
}
